import {useId, useState} from "react";
import {Badge, Box, Button, Flex, Table, Tbody, Td, Text, Th, Thead, Tr} from "@chakra-ui/react";
import type {ICreditAllowedActions, ICreditPackage, ICreditPurchase} from "../../models/interfaces/credits.interface.ts";
import {chipStyle, formatCredits, formatUtcDate, headerCellStyle, pressFeedback, statusToneStyles, tableSx, textButtonStyle} from "./historyTableStyles.ts";
import type {StatusTone} from "./historyTableStyles.ts";

const PAGE_SIZE = 10;

const PURPOSE_LABELS: Record<NonNullable<ICreditPurchase["purpose"]>, string> = {
  started: "One-time pack",
  initial_subscription: "Subscription start",
  renewal: "Renewal",
  upgrade: "Upgrade",
};

const usdFormatter = new Intl.NumberFormat("en-US", {style: "currency", currency: "USD"});

// The app runs in Chakra dark mode; the white billing panel needs explicit light colors on every control.
const refundButtonStyle = {
  ...pressFeedback,
  size: "sm",
  rounded: "lg",
  fontSize: "sm",
  fontWeight: "semibold",
  variant: "outline",
  bg: "white",
  borderColor: "n.300",
  color: "n.700",
  // The 32px button sits in a 48px row; the pseudo-element extends its pointer target to 40px (30px padding box + 10).
  _before: {content: '""', position: "absolute", insetX: 0, insetY: "-5px"},
} as const;

const simulatedTagStyle = {
  rounded: "md",
  px: 1.5,
  lineHeight: 5,
  fontSize: "xs",
  fontWeight: "medium",
  textTransform: "none",
  bg: "n.200",
  color: "n.400",
} as const;

const formatUsdCents = (cents: number): string => usdFormatter.format(cents / 100);

// Only unused units of a fulfilled core purchase are refundable (C1/C5): signup gifts, admin grants and
// pre-core (schema 1) purchases carry no refund entitlement.
const isRefundablePurchase = (purchase: ICreditPurchase, actions: ICreditAllowedActions): boolean =>
  actions.refund
  && purchase.schemaVersion === 2
  && purchase.status === "fulfilled"
  && purchase.purpose !== null
  && purchase.provider !== "manual"
  && purchase.refundableCredits > 0;

const humanizeId = (id: string): string => {
  const words = id.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const purchaseKindLabel = (purchase: ICreditPurchase): string => {
  if (purchase.purpose) return PURPOSE_LABELS[purchase.purpose];
  return purchase.provider === "manual" ? "Grant" : "Legacy purchase";
};

const purchaseAmountCents = (purchase: ICreditPurchase): number =>
  purchase.amountCents ?? Math.round(purchase.priceUsd * 100);

const purchaseAmountLabel = (purchase: ICreditPurchase): string => {
  const cents = purchaseAmountCents(purchase);
  return cents === 0 ? "Free" : formatUsdCents(cents);
};

// The row carries its refunds' totals and their mode: a refund pays back through the purchase's own provider (C6),
// so a simulated purchase's refunds are simulated and are marked like every other simulated amount.
const purchaseStatus = (purchase: ICreditPurchase): {label: string; tone: StatusTone} | null => {
  if (purchase.refundedCredits > 0) {
    const amount = formatUsdCents(purchase.refundedCents);
    return {
      label: `Refunded ${formatCredits(purchase.refundedCredits)} credits (${purchase.simulated ? `${amount}, simulated` : amount})`,
      tone: "refunded",
    };
  }
  switch (purchase.status) {
    case "pending":
      return {label: "Processing", tone: "processing"};
    case "failed":
      return {label: "Failed", tone: "failed"};
    case "fulfilled":
      return purchaseAmountCents(purchase) === 0 ? null : {label: "Paid", tone: "paid"};
  }
};

interface PurchaseHistoryProps {
  purchases: ICreditPurchase[] | null;
  isUnavailable: boolean;
  allowedActions: ICreditAllowedActions;
  packages: ICreditPackage[] | undefined;
  onRefundPurchase?: (purchase: ICreditPurchase) => void;
  onRetry?: () => void;
}

const PurchaseHistory = ({
  purchases,
  isUnavailable,
  allowedActions,
  packages,
  onRefundPurchase,
  onRetry,
}: PurchaseHistoryProps) => {
  const headingId = useId();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  if (purchases === null && !isUnavailable) return null;

  const history = purchases ? [...purchases].sort((a, b) => b.createdAt.localeCompare(a.createdAt)) : [];
  const packageNameFor = (purchase: ICreditPurchase): string =>
    packages?.find((pkg) => pkg.id === purchase.packageId)?.name ?? humanizeId(purchase.packageId);
  // A failed read outranks rows from an earlier one: a stale row could still offer a refund that was already spent.
  const hasMore = !isUnavailable && history.length > visibleCount;

  return (
    <Box as="section" aria-labelledby={headingId} mt={6} data-testid="purchase-history">
      <Text as="h3" id={headingId} className="text-balance" fontSize="md" fontWeight="semibold" color="n.700">
        Purchase history
      </Text>
      <Box mt={3} bg="white" rounded="xl" borderWidth="1px" borderColor="n.300" overflowX="auto">
        {isUnavailable ? (
          <Flex align="center" gap={1} minH={12} px={4}>
            <Text fontSize="sm" color="n.500">Purchase history is unavailable right now.</Text>
            {onRetry && <Button {...textButtonStyle} onClick={onRetry}>Retry</Button>}
          </Flex>
        ) : history.length === 0 ? (
          <Flex align="center" minH={12} px={4}>
            <Text fontSize="sm" color="n.500">No purchases yet.</Text>
          </Flex>
        ) : (
          <Table size="sm" aria-labelledby={headingId} whiteSpace="nowrap" sx={tableSx}>
            <Thead>
              <Tr>
                <Th {...headerCellStyle}>Date</Th>
                <Th {...headerCellStyle}>Purchase</Th>
                <Th {...headerCellStyle}>Type</Th>
                <Th {...headerCellStyle} isNumeric>Amount</Th>
                <Th {...headerCellStyle}>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {history.slice(0, visibleCount).map((purchase) => {
                const status = purchaseStatus(purchase);
                return (
                  <Tr key={purchase.id} h={12} data-testid={`purchase-row-${purchase.id}`}>
                    <Td color="n.500">
                      <time dateTime={purchase.createdAt}>{formatUtcDate(purchase.createdAt)}</time>
                    </Td>
                    <Td color="n.700" fontWeight="medium">
                      {`${packageNameFor(purchase)} - ${formatCredits(purchase.credits)} credits`}
                    </Td>
                    <Td color="n.500">{purchaseKindLabel(purchase)}</Td>
                    <Td isNumeric color="n.700" fontWeight="medium">
                      {purchase.simulated && purchaseAmountCents(purchase) !== 0 ? (
                        // The tag leads so every figure keeps the column's right edge; the space keeps copied text readable.
                        <Flex as="span" display="inline-flex" align="center" gap={2}>
                          <Badge {...simulatedTagStyle}>Simulated</Badge>
                          {" "}
                          <span>{purchaseAmountLabel(purchase)}</span>
                        </Flex>
                      ) : purchaseAmountLabel(purchase)}
                    </Td>
                    <Td>
                      {isRefundablePurchase(purchase, allowedActions) && onRefundPurchase ? (
                        <Button {...refundButtonStyle} onClick={() => onRefundPurchase(purchase)}>
                          {`Refund ${formatCredits(purchase.refundableCredits)} unused`}
                        </Button>
                      ) : status && (
                        <Badge {...chipStyle} {...statusToneStyles[status.tone]}>{status.label}</Badge>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>
      {hasMore && (
        <Flex justify="center" mt={2}>
          <Button {...textButtonStyle} onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
            Show more
          </Button>
        </Flex>
      )}
    </Box>
  );
};

export default PurchaseHistory;
